import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, AlertTriangle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { lookupResetToken, resetPassword } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh: refreshUser } = useAuth();
  const [state, setState] = useState({ status: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    lookupResetToken(token)
      .then((data) => {
        if (cancelled) return;
        setState({ status: "ready", email: data.email });
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail;
        let message = "This password-reset link is not valid.";
        if (code === 410) message = detail || "This reset link has expired or already been used.";
        else if (code === 404) message = "Reset link not found. Please request a new one.";
        setState({ status: "error", message });
      });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      toast.error("Password must be at least 8 characters and include a letter and a number");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token, password);
      // The reset endpoint issues a session cookie so the user is already signed in.
      // Refresh AuthContext so ProtectedRoute sees them.
      try { await refreshUser?.(); } catch { /* no-op */ }
      toast.success("Password updated — you're signed in");
      navigate("/projects", { replace: true });
    } catch (err) {
      const code = err?.response?.status;
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      if (code === 410 || code === 404) {
        setState({ status: "error", message: detail || "This reset link is no longer valid." });
      } else {
        toast.error(detail || "Could not update password");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state.status === "loading") {
    return (
      <div className="min-h-screen grid place-items-center text-[#A8A29E] text-sm" data-testid="reset-loading">
        Checking your reset link…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="min-h-screen fc-app-bg grid place-items-center px-4" data-testid="reset-error">
        <div className="fc-card p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 grid place-items-center mx-auto">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <h2 className="fc-heading text-lg text-[#1C1C1C] mt-4">Reset link unavailable</h2>
          <p className="text-sm text-[#57534E] mt-2">{state.message}</p>
          <Button
            data-testid="reset-error-back-btn"
            onClick={() => navigate("/auth", { replace: true })}
            className="mt-6 bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
          >
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen fc-app-bg grid place-items-center px-4" data-testid="reset-page">
      <div className="fc-card p-8 max-w-md w-full">
        <div className="w-12 h-12 rounded-full bg-sage-100 border border-sage-200 grid place-items-center">
          <Lock className="w-5 h-5 text-sage-700" />
        </div>
        <h2 className="fc-heading text-2xl text-[#1C1C1C] mt-5 leading-tight">
          Set a new password
        </h2>
        <p className="text-sm text-[#78716C] mt-2">
          Choose a new password for <strong className="text-[#1C1C1C]" data-testid="reset-email">{state.email}</strong>.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="fc-label mb-1.5 block">New password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#A8A29E] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="reset-password-input"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters with a letter and a number"
                className="fc-input h-11 pl-10 pr-10"
                required
                minLength={8}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#1C1C1C] transition-colors"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="fc-label mb-1.5 block">Confirm password</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#A8A29E] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="reset-confirm-input"
                type={show ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter the password"
                className="fc-input h-11 pl-10"
                required
                minLength={8}
              />
            </div>
          </div>
          {password && password === confirm && (
            <div className="text-[11px] text-emerald-600 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Passwords match
            </div>
          )}
          <Button
            type="submit"
            data-testid="reset-submit-btn"
            disabled={submitting}
            className="w-full h-11 rounded-lg bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] text-sm font-medium inline-flex items-center justify-center gap-2"
          >
            {submitting ? "Updating…" : (<>Update password <ArrowRight className="w-4 h-4" /></>)}
          </Button>
          <button
            type="button"
            onClick={() => navigate("/auth", { replace: true })}
            className="block w-full text-center text-[11px] text-[#78716C] hover:text-[#1C1C1C] underline underline-offset-2"
          >
            Back to sign in
          </button>
        </form>
      </div>
    </div>
  );
}
